/**
 * ATTDEF/ATTRIB value substitution → `RenderableEntity[]` adapter (TYPED).
 *
 * S10c Phase A + Phase B (see `blockFromEntity.ts` module header "Two-phase
 * child walk"). ATTDEF is not a top-level `DrawingEntity` kind — it lives
 * only inside `BlockDefinition.entities` — so `entityToRenderableTyped`
 * deliberately excludes it (`blockTransform.ts transformBlockChild` carve-out
 * 1 returns `null` for `"Attdef"`). This module owns its render instead.
 *
 * # AutoCAD parity (Autodesk DXF reference — ATTDEF/ATTRIB group 70 bit 2
 *   "constant"; this repo's own `attributeMutations.ts
 *   updateAttributeValueMutation`, which THROWS "Cannot edit constant
 *   attribute … create a new INSERT to change its value" — a constant
 *   attribute's value lives ONLY on the block definition, never per-instance)
 *
 *   - **Phase A** (variable ATTDEF, `flags.constant === false`): the
 *     per-INSERT ATTRIB `value` — matched by TAG, the only key
 *     `blockInsertAttributeSchema` carries (no ATTDEF-handle back-pointer,
 *     mirrors `updateAttributeValueMutation`'s own `child.tag === tag`
 *     lookup) — wins. EATTEDIT per-ATTRIB overrides
 *     (position/height/rotation/style/invisible) replace the ATTDEF template
 *     fields they cover; a field with no override inherits the template.
 *     Missing ATTRIB data falls back to the ATTDEF's own
 *     defaultValue/tag/"?" — never blank (matches the legacy
 *     `scene/renderAttribute.tsx` fallback chain, the dialog-preview twin of
 *     this render).
 *   - **Phase B** (constant ATTDEF, `flags.constant === true`): ALWAYS
 *     renders the ATTDEF's OWN `defaultValue` at every INSERT, VERBATIM —
 *     including an intentionally blank `""` (round-2 FINDING 2: a constant
 *     has no per-instance ATTRIB to fall back through, so `defaultValue` IS
 *     the final value, never resurrected to the tag/"?" the variable
 *     branch's "genuinely absent" fallback uses) — ignoring any stray
 *     ATTRIB with a matching tag (a constant value cannot vary per
 *     instance — the only way to change it is editing the BLOCK DEFINITION).
 *     NOTE — the PDF plot twin `print/pdf/converters/insertConverter.ts
 *     attributeToTextRun` has NO constant/variable split (it renders
 *     `insert.attributes` verbatim with no ATTDEF-defaultValue fallback for a
 *     missing tag); that is a pre-existing gap in the PDF path, out of this
 *     render-only slice's scope — not replicated here on purpose.
 *
 * # Placement — the `overridePosition` WORLD-vs-block-local invariant
 *
 * `BlockInsertAttribute.overridePosition` is the SYSTEM-WIDE discriminator
 * between the two placement regimes, and its semantic MUST agree with the
 * export encoder (`dxfBridge.attribExportPosition.ts resolveAttribExportGeometry`,
 * round-4 FINDING 1 + round-6 findings 1/3/4) or a round-trip drifts:
 *
 *   - **SET → a WORLD/absolute placed position** (already the resolved DXF
 *     group-10/20 point). The import parser fills it verbatim from the raw
 *     ATTRIB insertion point (`dxfBridge.drawingToDxf.ts`); a genuine EATTEDIT
 *     / AI reposition writes the world point the user picked
 *     (`updateAttributeValueMutation`). The export encoder returns it
 *     UNCHANGED as group 10/20. So the render MUST place the text AT that
 *     world point — it must NOT run it (or the ATTRIB's own world
 *     height/rotation) through the INSERT affine a SECOND time. Round-5
 *     FINDING 1 (P1): the previous code fed this world point through
 *     `transformBlockChild` as if it were block-local, so any
 *     translated/rotated/scaled INSERT double-transformed its attribute text
 *     (an imported ATTRIB at world (102,103) under an INSERT at (100,100)
 *     rendered near (202,203)). The world regime therefore uses the DXF
 *     group defaults the export writes for the unset sub-fields
 *     (`overrideHeight ?? 2.5`, `overrideRotation ?? 0`,
 *     `overrideStyle ?? "Standard"`) so render ⇔ export stay byte-consistent.
 *   - **UNSET → follow the ATTDEF template LIVE, in BLOCK-LOCAL space.** A
 *     value-only DDATTE edit (round-3 FINDING 1+4 — `updateAttributeValueMutation`
 *     deliberately leaves `overridePosition` `undefined`) and a freshly
 *     authored INSERT (`createBlockInsertEntityFull`, round-5 — also leaves it
 *     unset, matching that same precedent) both defer to the template so the
 *     attribute tracks the ATTDEF and the current INSERT transform. The
 *     template's OWN anchor is `alignmentPoint` (DXF 11/21) for any justified
 *     ATTDEF that carries one, `insertionPoint` (DXF 10/20) otherwise —
 *     round-2 FINDING 1, `resolveTemplateAnchor`, mirroring
 *     `textConverter.ts`'s TEXT anchor resolution. This block-local anchor
 *     THEN rides the IDENTICAL per-kind block affine every other text-like
 *     child uses (`transformBlockChild`'s `"Text"` case).
 *
 * Both regimes reuse ONE render pipeline: build a synthetic `TextEntity` and
 * feed it through `transformBlockChild` + `entityToRenderableTyped`, so the
 * scalar conventions (height·|scaleY|, rotation + the round-6 double-negative
 * fold, widthFactor·|scaleX|/|scaleY|) and the block-child layer inheritance
 * cannot drift from the Text sibling. The WORLD regime passes the OUTER
 * (residual) transform (`outer`, see next section) — the IDENTITY transform
 * for a top-level, non-grid INSERT (a geometric no-op: the world point/
 * rotation/height pass through untouched) — while the layer resolution +
 * leaf-adapter dispatch still run — one transform truth, zero duplicated
 * formulae, no double-transform.
 *
 * # Round-6 findings 2+5 — the OUTER-transform decomposition of a WORLD point
 *
 * "SET `overridePosition` → skip the INSERT affine" (round-5) was TOO COARSE:
 * it fed the world point through an UNCONDITIONAL identity, which is only
 * correct for a TOP-LEVEL, single-cell INSERT. The DXF group-10/20 an ATTRIB
 * carries is NOT the drawing WCS in general — it is the placed point in the
 * coordinate frame the ATTRIB is *authored in*, which already bakes in the
 * ATTRIB's OWN immediate INSERT affine but NOT the levels above it (Autodesk
 * DXF reference — ATTRIB is a full OCS entity stored with the block content;
 * `ezdxf add_auto_attribs` bakes `insert.matrix44() · attdef_position`, i.e.
 * the transform of the INSERT the ATTRIB belongs to). Two "levels above" the
 * round-5 identity wrongly discarded:
 *
 *   - **finding 2 (MINSERT):** a MINSERT stores ONE ATTRIB, positioned for the
 *     base (row 0/col 0) cell. AutoCAD repeats the WHOLE block content — the
 *     attribute included — at each grid cell, shifted by the col/row spacing
 *     in the block's own rotated/scaled frame. The identity dropped that
 *     per-cell grid offset, so every cell's label piled onto the base cell's
 *     absolute point while only the geometry moved.
 *   - **finding 5 (nested INSERT):** an INSERT that lives INSIDE a block
 *     definition stores its ATTRIB group-10 in that ENCLOSING block's local
 *     frame, NOT the drawing WCS. The identity rendered the label in raw
 *     block-definition coordinates while the nested geometry correctly rode
 *     the outer INSERT chain.
 *
 * Correct model: `overridePosition` skips ONLY the ATTRIB's OWN immediate
 * INSERT affine; it MUST still ride the accumulated OUTER transform — the
 * MINSERT grid-cell offset of that same INSERT (a placement of a REPETITION,
 * not the INSERT's "own" affine) AND every enclosing block-instance transform
 * above it (genuinely different world placements). "Skip own affine" ≠ "skip
 * ALL transforms in the chain". The render threads that residual as `outer`
 * (`AttribOuterTransform` — the transform+basePoint of the frame the
 * `overridePosition` lives in). `blockFromEntity`/`flattenTypedInsert` builds
 * it by construction:
 *   - top-level single INSERT → IDENTITY (world point is already final);
 *   - MINSERT cell → a pure translation by that cell's grid offset;
 *   - nested INSERT recursion → the PARENT level's full accumulated transform
 *     `t` (which itself already folds any MINSERT cell offset + outer chain),
 *     so the composition is correct to any nesting depth.
 * The block-LOCAL (unset) regime is unchanged: it rides the full `t`, which
 * already contains every level.
 *
 * NOTE (pre-existing, out of THIS render slice's scope): the PDF plot twin
 * (`print/pdf/converters/insertConverter.ts attributeToTextRun`) still treats a
 * SET `overridePosition` as block-local (it transforms it through the INSERT
 * matrix), so it double-transforms an IMPORTED ATTRIB exactly as this render
 * used to. That is a pre-existing defect (import has always stored a world
 * point) and is tracked separately — not reproduced here. The EXPLODE reducers
 * (`command-engine/blockMutations.ts`, `state/blockReducerHelpers.ts`) had the
 * SAME defect but were corrected to honour the world-vs-block-local invariant
 * in #470, via the shared `command-engine/attribExplodePlacement.ts` resolver.
 *
 * # Multiline fidelity boundary (flags.multiline, DXF 70 bit 32)
 *
 * The model (`attdefEntitySchema` / `blockInsertAttributeSchema`) stores the
 * resolved value as a FLAT string — dxf-io does not parse the AutoCAD
 * 2018+ embedded-MTEXT sub-object a genuine multiline ATTDEF/ATTRIB carries
 * (a pre-existing dxf-io/model gap, out of THIS render-only slice's scope;
 * no test fixture in this repo exercises the embedded-MTEXT group set). What
 * IS honoured with the data the model actually has: a literal embedded
 * newline in the resolved value string renders as STACKED TEXT lines
 * (AutoCAD's documented default "at least" MTEXT line pitch, 5/3 × nominal
 * height), each riding the SAME block affine as line 0 — so a multiline
 * value still rotates/scales correctly with its parent INSERT. The
 * overwhelming common case (a single-line value) is unaffected — exactly one
 * TEXT line, byte-identical to treating `flags.multiline` as absent.
 *
 * Round-4 FINDING 3 — the per-line stacking offset is computed in the TEXT's
 * OWN rotated local frame (`placement.rotation`, `rotateLocalOffset` below),
 * not the block's raw unrotated Y axis. AutoCAD's reading-order "next line is
 * below this one" always means "below" relative to the text's OWN baseline
 * direction — for a `rotation: 90` ATTDEF that direction is the block's local
 * +X axis, not -Y. The offset is computed HERE (before `transformBlockChild`
 * composes the parent INSERT's OWN additional rotation onto every line
 * identically) so it stacks correctly for ANY combination of the ATTDEF's own
 * rotation and the INSERT's rotation — exactly mirroring how `rotation:
 * placement.rotation` on `localChild` itself only carries the TEXT's local
 * rotation, with `transformBlockChild` adding the INSERT's rotation on top
 * (`blockTransform.ts` "Text rotation + rotationDeg").
 *
 * # Visibility
 *
 * An invisible attribute (ATTDEF `flags.invisible`, or a per-ATTRIB
 * `invisible` EATTEDIT override for a variable ATTDEF) renders NOTHING —
 * AutoCAD's ATTDISP=Normal default. This codebase has no ATTDISP sysvar yet
 * (grep-verified), so the default-hide behaviour is the only mode; adding
 * ATTDISP is a separate, out-of-scope feature.
 */

import { textEntitySchema } from "@dxf-ai-editor/drawing-model";
import {
  TEXT_GEN_FLAG_UPSIDE_DOWN,
  rotateLineStackOffset,
} from "@dxf-ai-editor/command-engine";
import type {
  AttdefEntity,
  BlockInsertAttribute,
  EntityColor,
} from "@dxf-ai-editor/drawing-model";

import type { RenderableEntity, Vec3 } from "../../types";

import { positiveOr, type AdapterContext } from "../adapterContext";

import { resolveTypedAnnotation } from "./annotativeResolve";
import {
  transformBlockChild,
  type BlockInsertTransform,
} from "./blockTransform";
import { entityToRenderableTyped } from "./index";

/**
 * AutoCAD's documented default MTEXT "at least" line pitch — 5/3 × nominal
 * text height — reused to stack a resolved multiline ATTRIB/ATTDEF value's
 * embedded lines (module header "Multiline fidelity boundary").
 */
const LINE_PITCH_FACTOR = 5 / 3;

/**
 * Round-5 FINDING 1 / round-6 findings 2+5 — the IDENTITY `BlockInsertTransform`.
 * Feeding a world point through `transformBlockChild` with this transform is a
 * geometric no-op (`(p − 0)·1` rotated 0 + 0 = p; height·|1| = height; rotation
 * + 0 = rotation; widthFactor·(1/1) = widthFactor) so the world position/
 * rotation/height pass through untouched. It is the OUTER transform of a
 * top-level, single-cell INSERT (`IDENTITY_ATTRIB_OUTER`).
 */
const WORLD_PLACEMENT_TRANSFORM: BlockInsertTransform = {
  insertion: { x: 0, y: 0, z: 0 },
  scale: { x: 1, y: 1, z: 1 },
  rotationDeg: 0,
};
const WORLD_PLACEMENT_BASE_POINT: Vec3 = { x: 0, y: 0, z: 0 };

/**
 * Round-6 findings 2+5 — the accumulated OUTER (residual) transform applied to
 * a WORLD (`overridePosition`) placement: the transform of the frame the
 * `overridePosition` lives in → drawing WCS (module header "The OUTER-transform
 * decomposition"). Everything ABOVE the ATTRIB's OWN immediate INSERT affine:
 * the MINSERT grid-cell offset of that INSERT plus every enclosing
 * block-instance transform. Applied with `transformBlockChildPoint(p,
 * basePoint, transform)` exactly like a block-local child, so the SAME scalar
 * conventions (height·|scaleY|, rotation + fold) carry the outer levels'
 * rotation/scale to the label's height/angle too (correct for the nested case,
 * a geometric no-op for the pure-translation MINSERT case and the identity
 * top-level case).
 */
export interface AttribOuterTransform {
  /** Transform from the `overridePosition`'s authoring frame → world. */
  transform: BlockInsertTransform;
  /** Base point of that same authoring frame (subtracted before the map). */
  basePoint: Vec3;
}

/**
 * The OUTER transform of a top-level, single-cell INSERT: the world
 * `overridePosition` is already final, so the residual is a geometric no-op.
 * The direct-unit callers of `attdefFromEntity`/`orphanAttributeFromEntity`
 * (single top-level INSERT) default to this; `flattenTypedInsert` threads the
 * real per-cell / per-nesting-level residual explicitly.
 */
export const IDENTITY_ATTRIB_OUTER: AttribOuterTransform = {
  transform: WORLD_PLACEMENT_TRANSFORM,
  basePoint: WORLD_PLACEMENT_BASE_POINT,
};

/**
 * Round-4 FINDING 3 / round-11 (mirror) — the "step down `distance` units"
 * pitch offset for a multiline ATTDEF/ATTRIB value's line `i`, rotated by
 * the TEXT's OWN local rotation and MIRRTEXT-gated. Now lives in
 * `@dxf-ai-editor/command-engine` as `rotateLineStackOffset`
 * (`mirrorRotationField.ts`) — extracted (jscpd dup-gate) as the ONE shared
 * truth this typed scene3d oracle and the PDF converter
 * (`insertAttributes.ts`) both call, rather than two independently-
 * maintained near-identical copies.
 */

/**
 * Resolve the per-INSERT ATTRIB matching one ATTDEF template by TAG — the
 * only key `blockInsertAttributeSchema` carries (no ATTDEF-handle
 * back-pointer). Mirrors `updateAttributeValueMutation`'s own lookup for the
 * COMMON (unique-tag) case.
 *
 * Round-4 FINDING 4 — the authoring path does not reject a BlockDefinition
 * with DUPLICATE ATTDEF tags (BATTMAN / hand-edited DXF), and a real INSERT
 * of such a block carries one ATTRIB row PER ATTDEF occurrence, in the SAME
 * order (mirrors real DXF/AutoCAD: `ezdxf add_auto_attribs` walks the block's
 * ATTDEFs in order and emits one ATTRIB per occurrence). A plain `.find()`
 * would return the FIRST matching row for EVERY duplicate-tag ATTDEF, so
 * every duplicated placeholder rendered the SAME (first) value and the
 * later rows were never consumed (and the orphan pass — `blockFromEntity.ts`
 * — then ALSO skipped them, since the tag as a whole was already "seen").
 * `occurrenceIndex` (the caller's running per-tag ATTDEF-visit counter, 0 for
 * the first ATTDEF with this tag, 1 for the second, …) selects the
 * matching-by-position ATTRIB row instead of always the first — byte-
 * identical to the previous behaviour when a tag is unique (occurrenceIndex
 * is always 0 there).
 */
function findMatchingAttribute(
  attdef: AttdefEntity,
  attributes: readonly BlockInsertAttribute[],
  occurrenceIndex: number,
): BlockInsertAttribute | undefined {
  let seen = 0;
  for (const a of attributes) {
    if (a.tag !== attdef.tag) continue;
    if (seen === occurrenceIndex) return a;
    seen++;
  }
  return undefined;
}

/**
 * Phase A / Phase B value resolution (module header). Falls back to the
 * ATTDEF's defaultValue/tag/"?" ONLY when the ATTRIB is genuinely absent
 * (no tag match on the owning INSERT). An explicit `attr.value === ""` —
 * DDATTE/EATTEDIT "clear attribute" stores `newValue: ""` verbatim as the
 * real `BlockInsertAttribute.value` (see `attributeMutations.ts
 * updateAttributeValueMutation`) — is a PRESENT value and must render
 * blank, not resurrect the stale default. A falsy `||` chain conflates
 * "absent" with "explicitly empty"; use an `attr !== undefined` presence
 * check instead so a cleared attribute renders nothing, matching the
 * document's actual state.
 *
 * Round-2 FINDING 2 — the SAME conflation existed on the constant branch: a
 * constant ATTDEF has NO per-instance ATTRIB to source from (module header
 * "Phase B" — its `defaultValue` IS the final value, always), so an
 * intentionally blank `defaultValue: ""` is PRESENT data, not absence. The
 * previous `attdef.defaultValue || attdef.tag || "?"` falsy chain
 * conflated the two and resurrected the tag as visible text on every
 * INSERT/MINSERT cell. There is no "absent vs explicitly empty" distinction
 * to make here at all (unlike the variable branch, which distinguishes a
 * missing ATTRIB row from a present-but-cleared one) — `defaultValue` is a
 * required string field (`attdefEntitySchema` — schema default `""`, never
 * `undefined`) and is used verbatim.
 */
function resolveAttributeValue(
  attdef: AttdefEntity,
  attr: BlockInsertAttribute | undefined,
): string {
  if (attdef.flags.constant) {
    return attdef.defaultValue;
  }
  if (attr !== undefined) return attr.value;
  return attdef.defaultValue || attdef.tag || "?";
}

/**
 * EATTEDIT per-ATTRIB visibility override wins for a variable ATTDEF; a
 * constant ATTDEF has no per-instance override (Phase B always uses the
 * ATTDEF's own flag — module header).
 */
function resolveInvisible(
  attdef: AttdefEntity,
  attr: BlockInsertAttribute | undefined,
): boolean {
  if (attdef.flags.constant) return attdef.flags.invisible;
  return attr?.invisible ?? attdef.flags.invisible;
}

/**
 * DXF horizontal justification (group 72, shared TEXT/ATTDEF enum) values
 * where the visual anchor is NOT the insertion point but the alignment
 * point (group 11/21):
 *   0 = Left (uses insertionPoint), 1 = Center, 2 = Right, 3 = Aligned,
 *   4 = Middle, 5 = Fit. Any nonzero horizontal OR nonzero vertical
 *   justification means the alignment point is the anchor, not the
 *   insertion point — this is the Autodesk DXF reference rule already
 *   implemented once as the reference pattern in
 *   `print/pdf/converters/textConverter.ts usesAlignmentPoint` (standalone
 *   TEXT anchor resolution); reproduced verbatim here (not imported — that
 *   module lives in the PDF-plot domain, this one in scene3d; a 2-line
 *   boolean predicate duplicated across an unrelated package boundary is
 *   incidental, not a semantic clone to extract).
 */
function usesAlignmentPoint(hJust: number, vJust: number): boolean {
  return hJust !== 0 || vJust !== 0;
}

/**
 * Round-2 FINDING 1 — resolve the ATTDEF template's own anchor point:
 * `alignmentPoint` (DXF 11/21) for any justified ATTDEF that actually
 * carries one, `insertionPoint` (DXF 10/20) otherwise (left/baseline
 * justification, or a justified ATTDEF whose DXF omitted 11/21 — the
 * insertion point IS the only anchor DXF gives us then). Previously this
 * module used `insertionPoint` unconditionally, so a justified ATTDEF (MC /
 * RIGHT / TR / …) whose insertion and alignment points diverge (a
 * hand-edited DXF, or a template where the ATTDEF was repositioned without
 * re-syncing 11/21) rendered at the wrong anchor. Mirrors the SAME
 * resolution `textConverter.ts textToPrimitives` performs for standalone
 * TEXT (see `usesAlignmentPoint` above).
 */
function resolveTemplateAnchor(attdef: AttdefEntity): {
  x: number;
  y: number;
} {
  return usesAlignmentPoint(attdef.hJustification, attdef.vJustification) &&
    attdef.alignmentPoint !== undefined
    ? attdef.alignmentPoint
    : attdef.insertionPoint;
}

/**
 * EATTEDIT per-ATTRIB placement overrides — block-LOCAL frame, same as the
 * ATTDEF template (module header "Placement"). A constant ATTDEF never
 * carries overrides (Phase B always renders the template verbatim). An
 * explicit EATTEDIT `overridePosition` (the user repositioned this specific
 * instance) wins over BOTH insertionPoint and alignmentPoint — it is
 * already the resolved anchor the user chose, not a template field to
 * re-resolve by justification.
 */
interface ResolvedPlacement {
  insertionPoint: { x: number; y: number };
  height: number;
  rotation: number;
  styleName: string;
  /**
   * Round-5 FINDING 1 — `true` when `insertionPoint`/`rotation`/`height` are a
   * WORLD/absolute placement (an explicit `overridePosition` — imported ATTRIB
   * or EATTEDIT reposition) that must NOT be re-run through the parent INSERT
   * affine; `false` when they are the block-local ATTDEF template that DOES
   * ride the INSERT transform (module header "Placement").
   */
  worldPlacement: boolean;
}
function resolvePlacement(
  attdef: AttdefEntity,
  attr: BlockInsertAttribute | undefined,
): ResolvedPlacement {
  if (attdef.flags.constant) {
    // A constant ATTDEF has no per-instance ATTRIB — always the block-local
    // template anchor, rides the INSERT transform.
    return {
      insertionPoint: resolveTemplateAnchor(attdef),
      height: attdef.height,
      rotation: attdef.rotation,
      styleName: attdef.styleName,
      worldPlacement: false,
    };
  }
  if (attr?.overridePosition !== undefined) {
    // Explicit WORLD placement (module header). Position/rotation/height/style
    // are absolute — render them verbatim (identity transform downstream),
    // using the SAME DXF-group defaults the export encoder writes for the
    // unset sub-fields so render ⇔ export stay byte-consistent
    // (`drawingAttribToDxfAttrib`: `overrideHeight ?? 2.5`, rotation omitted →
    // 0, style omitted → "Standard").
    return {
      insertionPoint: attr.overridePosition,
      height: attr.overrideHeight ?? 2.5,
      rotation: attr.overrideRotation ?? 0,
      styleName: attr.overrideStyle ?? "Standard",
      worldPlacement: true,
    };
  }
  // No explicit position — follow the ATTDEF template LIVE in block-local
  // space (value-only edit / freshly authored INSERT); rides the INSERT
  // transform. `overrideHeight/Rotation/Style`, when seeded from the template
  // by the create paths, resolve to the SAME `attdef.*` value either way.
  return {
    insertionPoint: resolveTemplateAnchor(attdef),
    height: attr?.overrideHeight ?? attdef.height,
    rotation: attr?.overrideRotation ?? attdef.rotation,
    styleName: attr?.overrideStyle ?? attdef.styleName,
    worldPlacement: false,
  };
}

/**
 * S10d — fold a matched annotative representation over a resolved ATTDEF
 * placement. Mirrors the legacy SVG `renderAttribute.tsx:89-97`: the per-scale
 * `attribute` context's insertion / rotation override the block-local template
 * fields, and the height renders at `templateHeight × effectiveScale` model
 * units. Returns the placement UNCHANGED for the non-annotative / no-match case
 * (`activeContext: null`, `effectiveScale: 1`) so golden parity holds when no
 * scale is active.
 *
 * Round-18 FINDING (attdefFromEntity:477) — `worldPlacement` MUST be CLEARED
 * when a matched context supplies a per-scale insertion. That insertion is a
 * BLOCK-LOCAL point (the `attribute` context stores the position in the block's
 * own frame — the legacy renderer draws it inside the block's transform group),
 * so it has to ride the FULL enclosing INSERT affine (`t`) like the template.
 * A variable ATTRIB with an `overridePosition` arrives here with
 * `worldPlacement: true`; the previous spread PRESERVED that flag, so
 * `renderAttributeValueLines` fed the block-local per-scale point through only
 * the OUTER residual (IDENTITY at the top level) instead of `t` — a top-level
 * INSERT at (100,100) rendered the per-scale attribute at (50,60) instead of
 * inside the block at (150,160). Clearing the flag (only when the context
 * actually replaced the position — `anno.insertion` present) routes the
 * block-local point back through the INSERT transform. When the context carries
 * NO insertion (defensive — the `attribute`/`text`/`mtext` kinds always do) the
 * original placement's regime is preserved untouched.
 */
function applyAnnotativePlacement(
  placement: ResolvedPlacement,
  anno: ReturnType<typeof resolveTypedAnnotation>,
): ResolvedPlacement {
  if (!anno.activeContext) return placement;
  const contextInsertion = anno.insertion;
  return {
    ...placement,
    insertionPoint: {
      x: contextInsertion?.x ?? placement.insertionPoint.x,
      y: contextInsertion?.y ?? placement.insertionPoint.y,
    },
    rotation: anno.rotation ?? placement.rotation,
    height: placement.height * anno.effectiveScale,
    // A block-local per-scale insertion rides the INSERT affine `t`, NOT the
    // OUTER residual — clear the world-placement flag a variable ATTRIB's
    // `overridePosition` set (see doc above). Only when the context actually
    // supplied the insertion; otherwise keep the original regime.
    worldPlacement:
      contextInsertion !== undefined ? false : placement.worldPlacement,
  };
}

/**
 * Style fields a resolved attribute value's rendered TEXT line(s) need,
 * beyond placement (module header). Shared by the ATTDEF-matched path
 * (`attdefFromEntity`, sourced from the ATTDEF template) and the orphan path
 * (`orphanAttributeFromEntity`, round-? FINDING 4 — sourced from the
 * PDF-style fallback since there is no ATTDEF template to inherit from).
 */
interface AttributeRenderStyle {
  layer: string;
  color: EntityColor;
  linetype: string;
  lineweight: string;
  transparency: string;
  linetypeScale?: number;
  widthFactor: number;
  obliqueAngle: number;
  hJustification: number;
  vJustification: number;
  generationFlags: number;
}

/**
 * Render a resolved attribute VALUE (already substituted / defaulted by the
 * caller) as one or more TEXT renderables through the enclosing INSERT's
 * block affine. Extracted from the original single-caller `attdefFromEntity`
 * body (round-? FINDING 4) so the orphan path (`orphanAttributeFromEntity`)
 * reuses the EXACT same per-line construction / multiline stacking /
 * transform-and-dispatch pipeline — one rendering truth for "a string value
 * anchored at a block-local point becomes TEXT renderable(s)", regardless of
 * whether an ATTDEF template supplied the style fields or the PDF-style
 * fallback did.
 *
 * Returns 1 renderable for a single-line value (the common case), N for an
 * embedded-newline multiline value, or `[]` when `lines` transforms to
 * nothing (defensive — callers gate visibility before calling this).
 *
 * @param value       the resolved display text (module header "Never
 *                    blank" for the ATTDEF-matched path).
 * @param placement   insertion point / height / rotation / style name,
 *                    already override-resolved by the caller.
 * @param style       layer/colour/linetype/lineweight/… (see
 *                    `AttributeRenderStyle`).
 * @param basePoint   the block definition's base point (same as every other
 *                    child transform).
 * @param t           the accumulated INSERT transform for this recursion
 *                    level (matches every other `transformBlockChild` call
 *                    site in `blockFromEntity.ts`).
 * @param identity    id/spaceId/parentLayer — same shape `transformBlockChild`
 *                    takes for every other child kind.
 */
function renderAttributeValueLines(
  value: string,
  placement: ResolvedPlacement,
  style: AttributeRenderStyle,
  basePoint: Vec3,
  t: BlockInsertTransform,
  outer: AttribOuterTransform,
  identity: { id: string; spaceId: string; parentLayer: string },
  ctx: AdapterContext,
  // GitHub Codex review PR #558 round 2 (round-9 GitHub bot follow-up) P2
  // — "Avoid double-scaling annotative WORLD attribute heights". `true`
  // when this attribute was ORIGINALLY a WORLD placement
  // (`attr?.overridePosition !== undefined`) but `applyAnnotativePlacement`
  // cleared `placement.worldPlacement` because a matched annotative
  // context supplied its OWN block-local insertion (round-18 FINDING /
  // round-3 P2's "positioning and handedness are independent" precedent —
  // see the `generationFlags` gate above this function's own caller for
  // the sibling fix). In that regime `effectiveTransform` below becomes
  // `t` (the FULL enclosing INSERT affine, needed to correctly map the
  // context's block-local point to world) — but `placement.height` is
  // ALREADY final (`applyAnnotativePlacement`'s own `height × effective
  // Scale`, matching a WORLD attribute's "absolute, never re-scaled by
  // the parent INSERT" contract, `attdefFromEntity.ts`'s module header
  // "Placement"). Without compensating, `transformBlockChild`'s generic
  // `height × |t.scale.y|` re-scales this ALREADY-final height a SECOND
  // time — SCALE-ing the enclosing Block then double(-or-worse)-counts:
  // once via `scaleEntityRadii.ts`'s WORLD-attribute `overrideHeight`
  // bump, once via the Block's OWN (unconditionally bumped) scaleY riding
  // through `t`.
  //
  // GitHub Codex review PR #558 round 2 (round-10 local read-only
  // follow-up) P2 — a NESTED WORLD attribute's `t` is the FULL
  // ACCUMULATED chain (enclosing levels ∘ the immediate INSERT), not just
  // the immediate level's own scale — the WORLD placement contract skips
  // ONLY the attribute's OWN immediate INSERT, but STILL inherits any
  // ENCLOSING block levels' scale (the SAME rule `outer.transform` itself
  // encodes for the non-context regime: IDENTITY for a top-level INSERT,
  // the enclosing chain's own transform for a nested one — module header
  // "Placement"). Dividing by the FULL `|t.scale.y|` (an earlier draft of
  // this fix) wrongly canceled the ENCLOSING levels' scale too — a parent
  // scaled 3× with an immediate child scaled 2× must still render at 3×
  // (matching what a non-annotative sibling attribute/child gets from
  // `outer.transform` alone), not the RAW unscaled height. Isolating the
  // immediate level's OWN contribution as `|t.scale.y| / |outer.transform.
  // scale.y|` (both magnitudes — the SAME scalar-magnitude convention
  // `transformBlockChild`'s own `heightScale = Math.abs(t.scale.y)` uses)
  // and dividing by ONLY that preserves the enclosing chain's scale while
  // still canceling the immediate level's redundant contribution. At the
  // top level `outer.transform` is IDENTITY (scale 1), so this reduces
  // exactly to the original (pre-round-10) `|t.scale.y|` division.
  worldHeightBypassesBlockScale: boolean,
): RenderableEntity[] {
  // `attdefEntitySchema.height` carries no positivity constraint (unlike
  // `textEntitySchema.height`'s `.nonnegative()`) — an imported malformed DXF
  // could carry a negative value. Clamp to 0 so the synthetic `.parse()`
  // below never throws; `textFromEntity`'s own `positiveOr(height, 2.5)`
  // then applies the SAME 0-falls-back-to-2.5 convention every other TEXT
  // entity gets, downstream of this clamp.
  const rawHeight = Math.max(0, placement.height);
  // A degenerate (zero) outer/immediate scale is its own separate
  // pre-existing edge case (the block collapses to nothing regardless) —
  // guard against dividing by zero rather than producing an Infinity/NaN
  // height.
  const outerHeightScale = Math.abs(outer.transform.scale.y) || 1;
  const immediateHeightScale = Math.abs(t.scale.y) / outerHeightScale || 1;
  const safeHeight = worldHeightBypassesBlockScale
    ? rawHeight / immediateHeightScale
    : rawHeight;

  // Round-3 FINDING 3 — the per-line Y pitch must use the SAME effective
  // height that will actually be rendered, not the raw (possibly-zero)
  // `safeHeight`. `safeHeight` of 0 still produces a VISIBLE line (each
  // synthetic Text child falls back to height 2.5 downstream via
  // `textFromEntity`'s own `positiveOr(height, 2.5)` — module header
  // "Multiline fidelity boundary"), but computing the stacking offset from
  // 0 collapsed every line of a zero/negative-height multiline ATTDEF onto
  // the SAME baseline (`i * 0 * LINE_PITCH_FACTOR === 0` for every `i`)
  // instead of stepping down line-by-line. Applying the identical
  // `positiveOr` fallback HERE keeps the pitch consistent with the height
  // each line will actually render at.
  const linePitchHeight = positiveOr(safeHeight, 2.5);

  // Multiline fidelity boundary (module header): split on a literal embedded
  // newline. `lines.length === 1` for the overwhelming common single-line
  // case — one TEXT renderable, no stacking.
  const lines = value.split(/\r\n|\r|\n/);

  // Round-5 FINDING 1 / round-6 findings 2+5 — a WORLD placement (explicit
  // `overridePosition`) skips the ATTRIB's OWN immediate INSERT affine but
  // STILL rides the accumulated OUTER transform (`outer` — the MINSERT cell
  // offset + any enclosing block chain; IDENTITY for a top-level single-cell
  // INSERT). A block-local template placement rides the real full INSERT affine
  // `t`. Either way the SAME `transformBlockChild` + `entityToRenderableTyped`
  // pipeline runs, so the layer inheritance / leaf dispatch is identical, and
  // the outer levels' rotation/scale correctly reach the label's height/angle
  // via the shared scalar conventions. The per-line stacking offset below is
  // computed in the text's OWN `placement.rotation` frame, correct under both
  // regimes (module header).
  const effectiveTransform = placement.worldPlacement ? outer.transform : t;
  const effectiveBasePoint = placement.worldPlacement
    ? outer.basePoint
    : basePoint;

  const out: RenderableEntity[] = [];
  for (let i = 0; i < lines.length; i++) {
    const lineId = lines.length === 1 ? identity.id : `${identity.id}-L${i}`;
    // Round-4 FINDING 3 — rotate the "step down" pitch offset by the TEXT's
    // OWN local rotation (`placement.rotation`) so it follows the text's
    // reading-down direction, not the block's raw unrotated Y axis; the
    // shared block affine below then rotates/scales the whole stack (offset
    // included) together with line 0, exactly like any other block-local
    // geometry.
    const lineOffset = rotateLineStackOffset(
      i * linePitchHeight * LINE_PITCH_FACTOR,
      placement.rotation,
      // GitHub Codex review PR #558 round 2 (round-11 local read-only
      // follow-up) P2 — `style.generationFlags` bit 4 is the SAME
      // upsideDown/local-Y-flip bit `textGeometry.ts` decodes for the
      // glyph mesh's own scale flip; the multi-line stacking direction
      // must reflect in lockstep (see `rotateLineStackOffset`'s own doc).
      (style.generationFlags & TEXT_GEN_FLAG_UPSIDE_DOWN) !== 0,
    );
    const localChild = textEntitySchema.parse({
      type: "Text",
      id: lineId,
      spaceId: identity.spaceId,
      layer: style.layer,
      color: style.color,
      linetype: style.linetype,
      lineweight: style.lineweight,
      transparency: style.transparency,
      ...(style.linetypeScale !== undefined
        ? { linetypeScale: style.linetypeScale }
        : {}),
      insertionPoint: {
        x: placement.insertionPoint.x + lineOffset.x,
        y: placement.insertionPoint.y + lineOffset.y,
      },
      height: safeHeight,
      rotation: placement.rotation,
      contents: lines[i],
      styleName: placement.styleName,
      widthFactor: style.widthFactor,
      obliqueAngle: style.obliqueAngle,
      hJustification: style.hJustification,
      vJustification: style.vJustification,
      generationFlags: style.generationFlags,
    });
    const transformed = transformBlockChild(
      localChild,
      effectiveBasePoint,
      effectiveTransform,
      {
        id: lineId,
        spaceId: identity.spaceId,
        parentLayer: identity.parentLayer,
      },
    );
    if (!transformed) continue;
    const renderable = entityToRenderableTyped(transformed, ctx);
    if (renderable) out.push(renderable);
  }
  return out;
}

/**
 * Render ONE ATTDEF child (Phase A substituted value, or Phase B constant
 * default) through the enclosing INSERT's block affine.
 *
 * Returns 1 renderable for a single-line value (the common case), N for an
 * embedded-newline multiline value, or `[]` when the resolved attribute is
 * invisible.
 *
 * @param attdef      the ATTDEF template (`BlockDefinition.entities` child).
 * @param attributes  the OWNING INSERT's `attributes[]` (top-level
 *                    `blockFromEntity` passes `insert.attributes`; a nested
 *                    Block child passes ITS OWN `attributes[]` — matches
 *                    `insertConverter.ts`'s "nested INSERT may itself carry
 *                    ATTRIBs" precedent).
 * @param occurrenceIndex  round-4 FINDING 4 — this ATTDEF's 0-based occurrence
 *                    count among ATTDEFs sharing its tag in the block
 *                    definition (the caller's running per-tag counter over
 *                    `blockDef.entities`, in walk order). Selects the
 *                    matching-by-position ATTRIB row when the block carries
 *                    duplicate ATTDEF tags; always 0 for the common
 *                    unique-tag case (byte-identical to a plain `.find()`).
 * @param basePoint   the block definition's base point (same as every other
 *                    child transform).
 * @param t           the accumulated INSERT transform for this recursion
 *                    level (matches every other `transformBlockChild` call
 *                    site in `blockFromEntity.ts`).
 * @param outer       round-6 findings 2+5 — the accumulated OUTER (residual)
 *                    transform applied to a WORLD `overridePosition` (MINSERT
 *                    cell offset + enclosing block chain); defaults to
 *                    `IDENTITY_ATTRIB_OUTER` (top-level single-cell INSERT).
 * @param identity    id/spaceId/parentLayer — same shape `transformBlockChild`
 *                    takes for every other child kind.
 */
export function attdefFromEntity(
  attdef: AttdefEntity,
  attributes: readonly BlockInsertAttribute[],
  occurrenceIndex: number,
  basePoint: Vec3,
  t: BlockInsertTransform,
  identity: { id: string; spaceId: string; parentLayer: string },
  ctx: AdapterContext,
  outer: AttribOuterTransform = IDENTITY_ATTRIB_OUTER,
): RenderableEntity[] {
  const attr = findMatchingAttribute(attdef, attributes, occurrenceIndex);
  if (resolveInvisible(attdef, attr)) return [];

  // S10d — resolve the active-scale annotative representation for this ATTDEF
  // (single truth: `resolveCannoscaleContext`; the `attribute` context kind).
  // Mirrors the legacy SVG `renderAttribute.tsx`: hide when no representation
  // matches at the active scale (ANNOALLVISIBLE=false), else the per-scale
  // insertion / rotation override the master placement and the height renders
  // at paper_height × effectiveScale. Non-annotative ATTDEF / no active scale →
  // `effectiveScale: 1`, `activeContext: null`, so the master placement is used
  // unchanged (golden parity intact).
  const anno = resolveTypedAnnotation(attdef.annotativeContexts, ctx);
  if (!anno.isVisible) return [];

  const value = resolveAttributeValue(attdef, attr);
  const placement = applyAnnotativePlacement(resolvePlacement(attdef, attr), anno);
  return renderAttributeValueLines(
    value,
    placement,
    {
      layer: attdef.layer,
      color: attdef.color,
      linetype: attdef.linetype,
      lineweight: attdef.lineweight,
      transparency: attdef.transparency,
      linetypeScale: attdef.linetypeScale,
      widthFactor: attdef.widthFactor,
      obliqueAngle: attdef.obliqueAngle,
      hJustification: attdef.hJustification,
      vJustification: attdef.vJustification,
      // GitHub Codex review PR #558 (post-rebase fresh pass) P2 — a WORLD
      // placement (`overridePosition` set) is absolute, verbatim, per-instance
      // data — it must NOT inherit the ATTDEF template's `generationFlags`
      // any more than it inherits the template's rotation/height/style
      // (`resolvePlacement`'s WORLD branch already applies the identical
      // override-or-DXF-default pattern: `overrideRotation ?? 0`,
      // `overrideHeight ?? 2.5`). A block-local (un-overridden) attribute
      // rides the template's OWN flags unchanged (out of MIRRTEXT's scope —
      // module header "Placement").
      //
      // Round-3 local review follow-up P2 — gated on `attr?.overridePosition
      // !== undefined` (the ORIGINAL, pre-annotative-adjustment regime), NOT
      // `placement.worldPlacement`: `applyAnnotativePlacement` CLEARS
      // `worldPlacement` whenever the active annotative context supplies its
      // own insertion point (that per-scale point rides the block-local
      // INSERT affine, not the WORLD outer residual — its own doc above), so
      // a WORLD attribute WITH an active annotative context would otherwise
      // read `placement.worldPlacement === false` and wrongly fall back to
      // the ATTDEF template's flags, silently un-mirroring a MIRROR-toggled
      // `overrideGenerationFlags` at that annotation scale. Positioning and
      // handedness are independent per-instance facts — the context changes
      // WHERE the attribute is drawn, never WHICH flags it carries.
      generationFlags:
        attr?.overridePosition !== undefined
          ? attr.overrideGenerationFlags ?? 0
          : attdef.generationFlags,
    },
    basePoint,
    t,
    outer,
    identity,
    ctx,
    // GitHub Codex review PR #558 round 2 (round-9 GitHub bot follow-up)
    // P2 — see `renderAttributeValueLines`'s own doc for the full
    // derivation. `attr?.overridePosition !== undefined` is the SAME
    // "originally WORLD" predicate the `generationFlags` gate just above
    // uses; `!placement.worldPlacement` detects that a matched annotative
    // context rerouted this specific render through the block-local `t`
    // path (`applyAnnotativePlacement` clears the flag only then).
    attr?.overridePosition !== undefined && !placement.worldPlacement,
  );
}

/**
 * Round-? FINDING 4 — render an ATTRIB that has NO matching ATTDEF in the
 * block definition (the ATTDEF was removed or renamed — e.g. BATTMAN — after
 * this INSERT was placed/EATTEDIT'd; the model preserves the now-orphaned
 * `BlockInsertAttribute` row on the INSERT verbatim, it is never pruned).
 * Without this, `flattenTypedInsert`'s Phase A/B walk (module header — driven
 * by iterating `blockDef.entities`, i.e. the ATTDEF side) never visits an
 * orphaned tag at all, so the INSERT's still-live attribute value silently
 * disappears from the scene3d render even though the document still carries
 * it (and the attribute dialogs / PDF-plot path still surface it).
 *
 * There is no ATTDEF template to inherit placement/style from, so this
 * mirrors the PDF-plot twin's own handling — `insertConverter.ts
 * attributeToTextRun`, which is ALWAYS attribute-driven (no ATTDEF-presence
 * branch at all, so every one of its ATTDEFs is implicitly the "orphan"
 * case from this module's perspective): the ATTRIB's own overrides win where
 * present, falling back to the SAME hardcoded defaults (height 2.5 / rotation
 * 0 / style "Standard" / position at the block origin / width-factor 1 / no
 * oblique / left-justified). Colour/linetype/layer are not modelled
 * per-ATTRIB at all (`blockInsertAttributeSchema` carries neither) — like the
 * PDF twin, which uses the OWNING INSERT's own resolved colour/layer
 * uniformly, this renders ByBlock colour/linetype so the standard BYBLOCK
 * resolution chain (`ctx.blockColor`/`ctx.blockLinetype`, already threaded by
 * every `flattenTypedInsert` call site) resolves to the enclosing INSERT's
 * own colour/linetype, and files under the enclosing INSERT's effective
 * layer (`identity.parentLayer`) — the SAME value `insertConverter.ts`
 * threads as `insertLayer`.
 *
 * @param attr        the orphaned `BlockInsertAttribute` (no ATTDEF in
 *                    `blockDef.entities` has a matching tag).
 * @param basePoint   see `attdefFromEntity`.
 * @param t           see `attdefFromEntity`.
 * @param outer       see `attdefFromEntity` — round-6 findings 2+5; an orphan's
 *                    own `overridePosition` is a WORLD/frame point exactly like
 *                    the matched path, so it rides the OUTER residual too.
 * @param identity    see `attdefFromEntity`.
 */
export function orphanAttributeFromEntity(
  attr: BlockInsertAttribute,
  basePoint: Vec3,
  t: BlockInsertTransform,
  identity: { id: string; spaceId: string; parentLayer: string },
  ctx: AdapterContext,
  outer: AttribOuterTransform = IDENTITY_ATTRIB_OUTER,
): RenderableEntity[] {
  // Mirrors `insertConverter.ts attributeToTextRun`'s `if (attr.invisible)
  // return null;` — an invisible orphan ATTRIB still renders nothing.
  if (attr.invisible) return [];
  // Round-5 FINDING 1 / round-6 findings 2+5 — an orphan ATTRIB's own
  // `overridePosition` (an imported ATTRIB whose ATTDEF was removed/renamed) is
  // a WORLD/frame point exactly like the matched path, so it skips the ATTRIB's
  // OWN immediate INSERT affine but STILL rides the accumulated OUTER residual
  // (`outer`); only the block-origin fallback (`{0,0}`, no override at all)
  // rides the full INSERT transform `t`.
  const placement: ResolvedPlacement = {
    insertionPoint: attr.overridePosition ?? { x: 0, y: 0 },
    height: attr.overrideHeight ?? 2.5,
    rotation: attr.overrideRotation ?? 0,
    styleName: attr.overrideStyle ?? "Standard",
    worldPlacement: attr.overridePosition !== undefined,
  };
  return renderAttributeValueLines(
    attr.value,
    placement,
    {
      layer: identity.parentLayer,
      color: { kind: "byBlock" },
      linetype: "ByBlock",
      lineweight: "ByLayer",
      transparency: "0",
      widthFactor: 1,
      obliqueAngle: 0,
      hJustification: 0,
      vJustification: 0,
      // GitHub Codex review PR #558 P2 — same override-or-0 pattern as the
      // matched-ATTDEF WORLD branch above (`attdefFromEntity`); an orphan
      // has no template to fall back to either way.
      generationFlags: attr.overrideGenerationFlags ?? 0,
    },
    basePoint,
    t,
    outer,
    identity,
    ctx,
    // GitHub Codex review PR #558 round 2 (round-9 GitHub bot follow-up)
    // P2 — an orphan ATTRIB has no ATTDEF template, so it never goes
    // through `applyAnnotativePlacement`/annotative-context resolution at
    // all (this function builds `placement` directly, above) — the
    // "context reroute" scenario `renderAttributeValueLines`'s new
    // parameter guards against is structurally impossible here.
    false,
  );
}
